"use client";

import { useRolesAndConfig } from "./api/endpoint.js";
import { ApiProvider, AuthToken, useApiReady } from "./api/api-provider.js";
import * as React from "react";
import { useUsers } from "./lib/api/user.js";
import {
  UsersManagementError,
  UsersManagementLoading,
  UsersManagement as UsersManagementPresentational,
} from "./lib/users-management.js";
import type {
  UsersManagementErrorProps,
  UsersManagementLoadingProps,
} from "./lib/users-management.js";
import { UsersManagementContextProvider } from "./lib/users-management-context.js";
import { useWorkOsApiUrl } from "./lib/widgets-context.js";
import { ErrorBoundary } from "./lib/error-boundary.js";
import { usePermissions } from "./lib/use-permissions.js";
import type { WidgetRootDomProps } from "./lib/utils.js";

interface UsersManagementProps extends WidgetRootDomProps {
  authToken: AuthToken;
}

const UsersManagement: React.FC<UsersManagementProps> = ({
  authToken,
  ...domProps
}) => {
  const baseUrl = useWorkOsApiUrl();

  return (
    <ErrorBoundary
      fallbackRender={({ error }) => (
        <UsersManagementError error={error} {...domProps} />
      )}
    >
      <ApiProvider
        widgetType="user-management"
        authToken={authToken}
        baseUrl={baseUrl}
      >
        <UsersManagementContextProvider>
          <UsersManagementImpl {...domProps} />
        </UsersManagementContextProvider>
      </ApiProvider>
    </ErrorBoundary>
  );
};

const UsersManagementImpl = (props: WidgetRootDomProps) => {
  const isApiReady = useApiReady();
  const rolesAndConfigQuery = useRolesAndConfig({
    query: { initialData: { roles: [], multipleRolesEnabled: false } },
  });
  const usersQuery = useUsers();
  const permissionsQuery = usePermissions("widgets:users-table:manage");

  if (
    permissionsQuery.isLoading ||
    usersQuery.isLoading ||
    rolesAndConfigQuery.isLoading ||
    usersQuery.isPending ||
    !isApiReady
  ) {
    return <UsersManagementLoading {...props} />;
  }

  if (
    permissionsQuery.isError ||
    usersQuery.isError ||
    rolesAndConfigQuery.isError
  ) {
    return (
      <UsersManagementError
        error={
          permissionsQuery.error ||
          usersQuery.error ||
          rolesAndConfigQuery.error
        }
        {...props}
      />
    );
  }

  return (
    <ErrorBoundary
      fallbackRender={({ error }) => (
        <UsersManagementError error={error} {...props} />
      )}
    >
      <UsersManagementPresentational
        userData={usersQuery.data}
        rolesData={rolesAndConfigQuery.data}
        isPending={usersQuery.isFetching}
        disableRolesFilter={
          rolesAndConfigQuery.isPending || rolesAndConfigQuery.isFetching
        }
        {...props}
      />
    </ErrorBoundary>
  );
};

export type {
  UsersManagementProps,
  UsersManagementLoadingProps,
  UsersManagementErrorProps,
};
export { UsersManagement, UsersManagementLoading, UsersManagementError };
